import type { AudienceMode, ProfileInput } from '../core/types';
import { sanitiseMode, sanitiseProfile, withUniqueIds } from './profileCodec';

/**
 * Share links.
 *
 * The whole profile is encoded into the URL **fragment**, never a query string.
 * That is a privacy decision, not a style one: a fragment is not sent to the
 * server, so it stays out of access logs, proxy logs, and `Referer` headers.
 * A query param would put someone's unpublished CV in a web server log on every
 * page load.
 *
 * There is still no such thing as a private URL — anyone holding the link has
 * the data — so the UI says so plainly next to the button.
 */

const PARAM = 'p';

/** Base64url: URL-safe and no `=` padding, so the link survives chat apps. */
function toBase64Url(input: string): string {
  const utf8 = new TextEncoder().encode(input);
  let binary = '';
  for (const byte of utf8) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export interface SharePayload {
  profile: ProfileInput;
  mode: AudienceMode;
}

/** Builds a shareable absolute URL for the given profile. */
export function encodeShareLink(payload: SharePayload, baseUrl: string): string {
  const json = JSON.stringify({ v: 1, mode: payload.mode, profile: payload.profile });
  const params = new URLSearchParams({ [PARAM]: toBase64Url(json) });
  // Strip any existing fragment so re-sharing does not stack payloads.
  const base = baseUrl.split('#')[0] ?? baseUrl;
  return `${base}#${params.toString()}`;
}

/**
 * Reads a payload out of a URL fragment, or returns `null` if there is none.
 *
 * Never throws: a malformed or truncated link is a `null`, not a crash. The
 * decoded object is untrusted, so it goes through `sanitiseProfile`.
 */
export function decodeShareLink(hash: string): SharePayload | null {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!fragment) return null;

  try {
    const encoded = new URLSearchParams(fragment).get(PARAM);
    if (!encoded) return null;
    const parsed: unknown = JSON.parse(fromBase64Url(encoded));
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return {
      profile: withUniqueIds(sanitiseProfile(record.profile)),
      mode: sanitiseMode(record.mode),
    };
  } catch {
    return null;
  }
}
